import { createApi } from "@reduxjs/toolkit/query/react";
import { VITE_API_V2_URL } from "codes";
import {
  FINANCE_STATUS,
  INVOICE_REQUEST_API_PATH,
  INVOICE_REQUEST_TYPE,
  type InvoiceRequestType,
} from "constants/invoice";
import { axiosBaseQuery } from "helpers/axiosbaseQuery";
import type { InvoiceOcrVerificationPayload } from "types/ocrVerification";

export interface BillOfLadingVerificationRequest {
  dealId?: string;
  documentId?: string;
  input?: {
    billOfLadingNumber?: string;
    bookingNumber?: string;
    containerNumbers?: string[];
    shipmentId?: string;
    carrierName?: string;
    scac?: string;
    vesselName?: string;
  };
  linkedDocuments?: {
    commercialInvoiceId?: string;
    purchaseOrderId?: string;
  };
  options?: {
    queryOfficialCarrier?: boolean;
    queryAggregatorFallback?: boolean;
    queryVesselAis?: boolean;
    queryVesselEligibility?: boolean;
    querySanctions?: boolean;
    checkDuplicatePresentation?: boolean;
    allowManualReview?: boolean;
  };
}

export interface BillOfLadingVerificationPayload {
  verificationId?: string;
  cacheHit?: boolean;
  runCount?: number;
  lastUpdatedAt?: string | null;
  nextRefreshAt?: string | null;
  status?: string;
  result?: string;
  tierA?: { score?: number; confidence?: string };
  shipmentFound?: boolean;
  trackingStatus?: string;
  evidenceScope?: string;
  resolvedCarrier?: { name?: string; code?: string; confidence?: number };
  shipment?: {
    carrier?: { name?: string; code?: string; slug?: string };
    containerSummary?: {
      checked?: number;
      found?: number;
      allMatched?: boolean;
    };
    vessel?: { name?: string; imoNumber?: string; voyageNumber?: string };
    containers?: Array<{ containerNumber?: string }>;
    transport?: {
      portOfLoading?: string;
      portOfDischarge?: string;
      estimatedArrival?: string;
    };
    status?: string;
    events?: Array<{
      eventCode?: string;
      date?: string;
      eventType?: string;
      description?: string;
      timestamp?: string;
      location?: string;
      actual?: boolean;
    }>;
    externalTrackingUrl?: string;
  };
  checks?: Array<{
    code?: string;
    status?: string;
    scoreAwarded?: number;
    maximumScore?: number;
    documentValue?: unknown;
    providerValue?: unknown;
    evidence?: string[];
  }>;
  warnings?: Array<{ message?: string }>;
  manualReview?: { required?: boolean; reasons?: string[] };
  duplicateCheck?: {
    exactMatches?: Array<{ matchedField?: string; flag?: string }>;
    fuzzyMatches?: Array<{ matchedField?: string; flag?: string }>;
  };
}

const normalizeBillOfLadingVerificationResponse = (
  response: any,
): BillOfLadingVerificationPayload => {
  const firstData = response?.data ?? response;
  const source = [
    firstData?.verification,
    firstData?.shipment_verification,
    firstData?.bl_verification,
    firstData?.data,
    firstData?.result,
  ].find(
    (candidate) => candidate && typeof candidate === "object",
  ) ?? firstData;

  if (!source || typeof source !== "object") {
    return {};
  }

  const reportSource = source.trade_finance_report ?? source.report ?? source;
  const shipmentSource =
    source.shipment ??
    source.shipment_data ??
    reportSource.shipment ??
    reportSource.shipment_data ??
    {};
  const transportSource = shipmentSource.transport ?? shipmentSource.transport_info ?? {
    portOfLoading: shipmentSource.port_of_loading ?? source.port_of_loading ?? reportSource.port_of_loading,
    portOfDischarge: shipmentSource.port_of_discharge ?? source.port_of_discharge ?? reportSource.port_of_discharge,
  };
  const containerSummarySource =
    shipmentSource.containerSummary ??
    shipmentSource.container_summary ??
    source.containerSummary ??
    source.container_summary;

  return {
    ...source,
    verificationId:
      source.verificationId ?? source.verification_id ?? source.id ??
      firstData?.verificationId ?? firstData?.verification_id,
    cacheHit:
      source.cacheHit ?? source.cache_hit ?? firstData?.cacheHit ?? firstData?.cache_hit,
    status: source.status ?? source.verification_status,
    result:
      source.result ??
      source.verification_result ??
      source.verification_status ??
      source.status,
    tierA: reportSource.tierA ?? reportSource.tier_a ?? source.tierA ?? source.tier_a
      ? {
          ...(reportSource.tierA ?? reportSource.tier_a ?? source.tierA ?? source.tier_a),
          score: (reportSource.tierA ?? reportSource.tier_a ?? source.tierA ?? source.tier_a).score ??
            (reportSource.tierA ?? reportSource.tier_a ?? source.tierA ?? source.tier_a).score_awarded,
          confidence: (reportSource.tierA ?? reportSource.tier_a ?? source.tierA ?? source.tier_a).confidence ??
            (reportSource.tierA ?? reportSource.tier_a ?? source.tierA ?? source.tier_a).confidence_level,
        }
      : undefined,
    shipmentFound: source.shipmentFound ?? source.shipment_found ?? reportSource.shipmentFound ?? reportSource.shipment_found,
    trackingStatus: source.trackingStatus ?? source.tracking_status ?? reportSource.trackingStatus ?? reportSource.tracking_status,
    evidenceScope: source.evidenceScope ?? source.evidence_scope ?? reportSource.evidenceScope ?? reportSource.evidence_scope,
    resolvedCarrier: source.resolvedCarrier ?? source.resolved_carrier ?? reportSource.resolvedCarrier ?? reportSource.resolved_carrier,
    shipment: (source.shipment ?? source.shipment_data) ||
      reportSource.shipment || reportSource.shipment_data || source.carrier ||
      reportSource.carrier || source.resolvedCarrier || source.container_summary || source.port_of_loading
      ? {
          ...shipmentSource,
          carrier: shipmentSource.carrier ?? source.carrier ?? reportSource.carrier ?? source.resolvedCarrier,
          containerSummary: containerSummarySource,
          vessel: shipmentSource.vessel ?? source.vessel ?? reportSource.vessel,
          transport: transportSource,
          events: shipmentSource.events ?? source.events,
        }
      : undefined,
    checks: Array.isArray(reportSource.checks ?? reportSource.verification_checks ?? source.checks ?? source.verification_checks)
      ? (reportSource.checks ?? reportSource.verification_checks ?? source.checks ?? source.verification_checks).map((check: any) => ({
          ...check,
          code: check.code ?? check.check_code,
          status: check.status ?? check.result,
          documentValue: check.documentValue ?? check.document_value,
          providerValue: check.providerValue ?? check.provider_value,
        }))
      : undefined,
    warnings: source.warnings ?? source.warning_messages ?? reportSource.warnings ?? reportSource.warning_messages,
    manualReview: source.manualReview ?? source.manual_review ?? reportSource.manualReview ?? reportSource.manual_review,
    duplicateCheck: source.duplicateCheck ?? source.duplicate_check ?? reportSource.duplicateCheck ?? reportSource.duplicate_check,
  };
};

export const getHistoricalInvoices = async () => {
  try {
    const baseUrl = `${VITE_API_V2_URL}/`;
    const response = await fetch(`${baseUrl}dashboard/historical-invoices`);
    const data = await response.json();
    return {
      historicalData: Array.isArray(data) ? data : data?.data || [],
    };
  } catch (error) {
    return { historicalData: [] };
  }
};

export const investor_v2 = createApi({
  reducerPath: "investor_v2",
  tagTypes: [
    "TABLE_VALUES",
    "TRANSACTION_LADGER",
    "AUM_DETAILS",
    "OPERATIONS_INVOICES",
  ],
  baseQuery: axiosBaseQuery({
    baseUrl: `${VITE_API_V2_URL}/`,
  }),
  endpoints: (builder) => ({
    GetInvoice: builder.query<any, { endpoint: string }>({
      query: (params) => {
        const cleanedEndpoint = params.endpoint.startsWith("/")
          ? params.endpoint.substring(1)
          : params.endpoint;
        return {
          url: `${cleanedEndpoint}`,
          method: "get",
        };
      },
      providesTags: () => ["TABLE_VALUES"],
      transformResponse: (response) => {
        if (!response) {
          return {
            invoices: [],
            funders: [],
            total_cash_available: 0,
          };
        }

        if (response.invoices !== undefined) {
          return {
            invoices: Array.isArray(response.invoices) ? response.invoices : [],
            funders: Array.isArray(response.funders) ? response.funders : [],
            total_cash_available: response.total_cash_available || 0,
          };
        }

        if (response.data) {
          return {
            invoices: Array.isArray(response.data.invoices)
              ? response.data.invoices
              : [],
            funders: Array.isArray(response.data.funders)
              ? response.data.funders
              : [],
            total_cash_available: response.data.total_cash_available || 0,
          };
        }

        if (response.data && Array.isArray(response.data)) {
          return {
            invoices: response.data,
            funders: [],
            total_cash_available: response.total_cash_available || 0,
          };
        }

        return {
          invoices: [],
          funders: [],
          total_cash_available: 0,
        };
      },
    }),

    GetInvestorsSummary: builder.query<any, { year?: string }>({
      query: (params) => {
        const { year = "all" } = params;
        return {
          url: `dashboard/investors-summary?year=${year}`,
          method: "get",
        };
      },
      providesTags: () => ["TABLE_VALUES"],
      transformResponse: (response) => {
        if (!response) {
          return {
            invoices: [],
            funders: [],
            total_cash_available: 0,
          };
        }
        return response.data ? response.data : response;
      },
    }),

    GetTransactionLadger: builder.query<any, { transparams: any }>({
      query: ({ transparams }) => ({
        url: `dashboard/transactions${transparams}`,
        method: "GET",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["TRANSACTION_LADGER"],
    }),

    GetAllTransactions: builder.query<any, { transparams: any }>({
      query: ({ transparams }) => ({
        url: `dashboard/transactions${transparams}`,
        method: "GET",
      }),
      transformResponse: (response) => response?.data,
      providesTags: ["TRANSACTION_LADGER"],
    }),

    GetAUMDetails: builder.query<any, { currency: string; funder_id?: string }>(
      {
        query: ({ currency, funder_id }) => {
          let url = `aum/currencies/${currency}`;
          if (funder_id) {
            url += `?funder_id=${funder_id}`;
          }
          return {
            url,
            method: "get",
          };
        },
        transformResponse: (response) => response.data,
        providesTags: ["AUM_DETAILS"],
      },
    ),

    GetImporters: builder.query<any, any>({
      query: (accountType) => ({
        url: `dashboard/${accountType}`,
        method: "get",
      }),
      providesTags: () => ["TRANSACTION_LADGER"],
      transformResponse: (response) => response?.data,
    }),

    GetSector: builder.query<any, void>({
      query: () => ({
        url: `dashboard/by/industry`,
        method: "get",
      }),
      providesTags: () => ["TRANSACTION_LADGER"],
      transformResponse: (response) => response?.data,
    }),

    ExportAllTransactions: builder.mutation<any, { transparams: string }>({
      query: ({ transparams }) => ({
        url: `dashboard/transactions${transparams}`,
        method: "GET",
      }),
      transformResponse: (response) => response?.data,
    }),

    GetAllInvoices: builder.query<any, { invoicesparams: any }>({
      query: ({ invoicesparams }) => {
        const rawParams =
          typeof invoicesparams === "string" ? invoicesparams : "";
        const paramsString = rawParams.startsWith("?")
          ? rawParams.slice(1)
          : rawParams;
        const searchParams = new URLSearchParams(paramsString);

        const normalizedParams = searchParams.toString();

        return {
          url: `operations/invoices${normalizedParams ? `?${normalizedParams}` : ""}`,
          method: "GET",
        };
      },
      transformResponse: (response) => response?.data,
      providesTags: ["OPERATIONS_INVOICES"],
    }),

    GetInvocieDetails: builder.query<any, string>({
      query: (invocie_id) => ({
        url: `operations/invoices/${invocie_id}`,
        method: "get",
      }),
      providesTags: () => ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    //Invocie verifaication APIs

    GetInvoiceVerification: builder.query<any, string>({
      query: (invoice_id) => ({
        url: `operations/invoice/${invoice_id}/invoice-verification`,
        method: "get",
      }),
      providesTags: () => ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    UpdateInvoiceVerification: builder.mutation<
      any,
      { invoice_id: string; data: any }
    >({
      query: ({ invoice_id, data }) => ({
        url: `operations/invoice/${invoice_id}/invoice-verification`,
        method: "patch",
        data: data,
      }),
      invalidatesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    AddinvoiceVerification: builder.mutation<
      any,
      { invoice_id: string; data: any }
    >({
      query: ({ invoice_id, data }) => ({
        url: `operations/invoice/${invoice_id}/invoice-verification`,
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    // Instructions for Ops APIs

    GetInvoiceInstructionsForOps: builder.query<any, string>({
      query: (invoice_id) => ({
        url: `operations/invoice/${invoice_id}/instructions-for-ops`,
        method: "get",
      }),
      providesTags: () => ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    UpdateInvoiceInstructionsForOps: builder.mutation<
      any,
      { invoice_id: string; data: any }
    >({
      query: ({ invoice_id, data }) => ({
        url: `operations/invoice/${invoice_id}/instructions-for-ops`,
        method: "patch",
        data: data,
      }),
      invalidatesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    AddInvoiceInstructionsForOps: builder.mutation<
      any,
      { invoice_id: string; data: any }
    >({
      query: ({ invoice_id, data }) => ({
        url: `operations/invoice/${invoice_id}/instructions-for-ops`,
        method: "POST",
        data: data,
      }),
      invalidatesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    GetInvoiceInteractionOps: builder.query<any, string>({
      query: (invoice_id) => ({
        url: `operations/invoices/${invoice_id}/interaction-ops`,
        method: "get",
      }),
      providesTags: () => ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    //operations invoice Details
    GetInvoiceDetails: builder.query<any, string>({
      query: (invoice_id) => ({
        url: `operations/invoices/${invoice_id}`,
        method: "get",
      }),
      providesTags: () => ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),
    GetInvoiceDocumentVerification: builder.query<InvoiceOcrVerificationPayload, string>({
      query: (invoice_id) => ({
        url: `document-verification/invoices/${invoice_id}/verification`,
        method: "get",
        keepUnusedDataFor: 30,
      }),
      providesTags: () => ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data ?? response,
    }),
    CreateBillOfLadingVerification: builder.mutation<
      BillOfLadingVerificationPayload,
      BillOfLadingVerificationRequest
    >({
      query: (data) => ({
        url: "bl-verification/verifications",
        method: "POST",
        data,
      }),
      transformResponse: normalizeBillOfLadingVerificationResponse,
    }),
    GetBillOfLadingVerification: builder.query<
      BillOfLadingVerificationPayload,
      string
    >({
      query: (verificationId) => ({
        url: `bl-verification/verifications/${verificationId}`,
        method: "GET",
      }),
      transformResponse: normalizeBillOfLadingVerificationResponse,
      keepUnusedDataFor: 60,
    }),
    GetLatestBillOfLadingVerification: builder.query<
      BillOfLadingVerificationPayload | null,
      { documentId?: string; dealId?: string; invoiceDocumentId?: string; purchaseOrderDocumentId?: string; blNumber?: string; bookingNumber?: string; containerNumbers?: string[] }
    >({
      query: ({ documentId, dealId, invoiceDocumentId, purchaseOrderDocumentId, blNumber, bookingNumber, containerNumbers }) => ({
        url: "bl-verification/verifications",
        method: "GET",
        params: { document_id: documentId, deal_id: dealId, invoice_document_id: invoiceDocumentId, purchase_order_document_id: purchaseOrderDocumentId, bl_number: blNumber, booking_number: bookingNumber, container_numbers: containerNumbers?.join(",") },
      }),
      transformResponse: (response: any) => (response && "data" in response ? response.data : response) == null ? null : normalizeBillOfLadingVerificationResponse(response),
      keepUnusedDataFor: 0,
    }),
    ReverifyBillOfLadingVerification: builder.mutation<
      BillOfLadingVerificationPayload,
      string
    >({
      query: (verificationId) => ({
        url: `bl-verification/verifications/${verificationId}/reverify`,
        method: "POST",
      }),
      transformResponse: normalizeBillOfLadingVerificationResponse,
    }),

    //operations invoice Details
    GetAllocatedFunderDetails: builder.query<any, string>({
      query: (allocationId) => ({
        url: `finance/allocations/${allocationId}`,
        method: "get",
      }),
      providesTags: () => ["OPERATIONS_INVOICES"],
      transformResponse: (response) => {
        return response;
      },
    }),
    getFunderBuyerSupplierLimitDetails: builder.query<
      any,
      { invoiceId: string; funderId: string }
    >({
      query: ({ invoiceId, funderId }) => ({
        url: `finance/invoice/${invoiceId}/funder/${funderId}`,
        method: "GET",
      }),
      providesTags: () => ["OPERATIONS_INVOICES"],
      transformResponse: (response) => {
        return response?.data;
      },
    }),

    // Update invoice
    UpdateInvoice: builder.mutation<
      any,
      {
        invoice_id: string;
        data: {
          assigned_to?: string;
          finance_status?: typeof FINANCE_STATUS.PENDING;
          move_to_allocation?: boolean;
          phase?: string;
          phase_status?: string;
          status?: string;
          invoice_processing_status_reason?: string;
        };
      }
    >({
      query: ({ invoice_id, data }) => ({
        url: `operations/invoices/${invoice_id}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    // Update invoice basic information
    UpdateInvoiceBasicInfo: builder.mutation<
      any,
      {
        invoice_id: string;
        data: any;
      }
    >({
      query: ({ invoice_id, data }) => ({
        url: `operations/invoices/${invoice_id}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response: any) => {
        // Handle both response.data and direct data
        return response?.data || response;
      },
    }),
    // map info
    GetInvoiceMapInfo: builder.query<any, string>({
      query: (invoice_id) => ({
        url: `operations/invoices/${invoice_id}/map-info`,
        method: "get",
      }),
      providesTags: () => ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),
    //templates of the buyer confirmation
    GetAllTemplates: builder.query<any, string>({
      query: (accountRelationshipId) => ({
        url: `operations/account-relationships/${accountRelationshipId}/email-templates/invoice-acknowledgement`,
        method: "get",
      }),
      providesTags: () => ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),
    //templates of the buyer confirmation
    GetAllTemplatesNOA: builder.query<any, string>({
      query: (accountRelationshipId) => ({
        url: `operations/account-relationships/${accountRelationshipId}/email-templates/noa`,
        method: "get",
      }),
      providesTags: () => ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    GetInvoiceRequestTemplates: builder.query<
      any,
      { accountRelationshipId: string; requestType: InvoiceRequestType }
    >({
      query: ({ accountRelationshipId, requestType }) => ({
        url: `operations/account-relationships/${accountRelationshipId}/email-templates/${requestType === INVOICE_REQUEST_TYPE.BUYER_REQUEST ? INVOICE_REQUEST_API_PATH.BUYER_REQUEST : INVOICE_REQUEST_API_PATH.SLA}`,
        method: "get",
      }),
      providesTags: () => ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),
    GetTemplateDetails: builder.mutation<
      any,
      { relationshipId: string; templateId: string; invoiceIds: string[] }
    >({
      query: ({ relationshipId, templateId, invoiceIds }) => ({
        url: `operations/account-relationships/${relationshipId}/email-templates/invoice-acknowledgement/${templateId}`,
        method: "POST",

        data: invoiceIds,

        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),
    GetInvoiceRequestTemplateDetails: builder.mutation<
      any,
      {
        relationshipId: string;
        templateId: string;
        invoiceIds: string[];
        requestType: InvoiceRequestType;
      }
    >({
      query: ({ relationshipId, templateId, invoiceIds, requestType }) => ({
        url: `operations/account-relationships/${relationshipId}/email-templates/${requestType === INVOICE_REQUEST_TYPE.BUYER_REQUEST ? INVOICE_REQUEST_API_PATH.BUYER_REQUEST : INVOICE_REQUEST_API_PATH.SLA}/${templateId}`,
        method: "POST",
        data: invoiceIds,
        headers: {
          "Content-Type": "application/json",
        },
      }),
      invalidatesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    // Fetch supporting documents for a set of invoices
    AllInvoiceDocuments: builder.mutation<any, { invoiceIds: string[] }>({
      query: ({ invoiceIds }) => ({
        url: `operations/invoices/invoice-supporting-documents`,
        method: "POST",
        data: invoiceIds,
        headers: { "Content-Type": "application/json" },
      }),
      transformResponse: (response) => response?.data,
    }),

    // Update funder available balance
    UpdateFunderBalance: builder.mutation<any, { amount: string | number }>({
      query: ({ amount }) => ({
        url: `funder/available-balance`,
        method: "POST",
        data: { amount },
      }),
      invalidatesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    // Get funder details by ID
    GetFunderDetails: builder.query<any, string>({
      query: (funder_id) => ({
        url: `funder/${funder_id}/`,
        method: "GET",
      }),
      providesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    // Submit funder allocations for invoice
    SubmitFunderAllocations: builder.mutation<
      any,
      {
        invoice_id: string;
        allocations: Array<{
          funder_id: string;
          allocated_amount: number;
          mpr: number;
          irwa: number;
          funder_allocated?: boolean;
        }>;
      }
    >({
      query: ({ invoice_id, allocations }) => ({
        url: `finance/allocations/${invoice_id}`,
        method: "POST",
        data: allocations,
        headers: { "Content-Type": "application/json" },
      }),
      invalidatesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    // Update funder allocations for invoice
    UpdateFunderAllocations: builder.mutation<
      any,
      {
        invoice_id: string;
        allocations: Array<{
          funder_id: string;
          allocated_amount: number;
          mpr: number;
          irwa: number;
          funder_allocated?: boolean;
        }>;
      }
    >({
      query: ({ invoice_id, allocations }) => ({
        url: `finance/funder-allocation/${invoice_id}/reallocate`,
        method: "PATCH",
        data: allocations,
        headers: { "Content-Type": "application/json" },
      }),
      invalidatesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    // Get funder allocations
    GetFunderAllocations: builder.query<any, void>({
      query: () => ({
        url: `finance/allocations`,
        method: "GET",
      }),
      providesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    // Send email template with FormData
    SendEmailTemplate: builder.mutation<
      any,
      { formData: FormData; type: string; templateId?: string }
    >({
      query: ({ formData, type }) => ({
        url: `operations/send-email?type=${type}`,
        method: "POST",
        data: formData,
        headers: { "Content-Type": "multipart/form-data" },
      }),
      invalidatesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),
    // Approve or Reject allocation
    AllocationDecision: builder.mutation<
      any,
      {
        inv_id: string;
        action: "approve" | "reject";
        note?: string;
      }
    >({
      query: ({ inv_id, action, note }) => ({
        url: `finance/allocations/${inv_id}/decision`,
        method: "POST",
        data: {
          action,
          ...(note && { note }),
        },
        headers: { "Content-Type": "application/json" },
      }),
      invalidatesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    // Cancel single allocation by allocation id
    CancelAllocation: builder.mutation<
      any,
      { allocation_id: string; data: { action: string; note?: string } }
    >({
      query: ({ allocation_id, data }) => ({
        url: `finance/allocation/${allocation_id}`,
        method: "POST",
        data,
        headers: { "Content-Type": "application/json" },
      }),
      invalidatesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    // Update allocation status by allocation id
    UpdateAllocationStatus: builder.mutation<
      any,
      { allocation_id: string; status: string }
    >({
      query: ({ allocation_id, status }) => ({
        url: `finance/allocation/${allocation_id}/status`,
        method: "PATCH",
        data: { status },
        headers: { "Content-Type": "application/json" },
      }),
      invalidatesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    // Patch allocation by allocation id
    PatchAllocation: builder.mutation<
      any,
      { allocation_id: string; data: Record<string, any> }
    >({
      query: ({ allocation_id, data }) => ({
        url: `finance/allocation/${allocation_id}`,
        method: "PATCH",
        data,
        headers: { "Content-Type": "application/json" },
      }),
      invalidatesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    // Buyback allocation
    BuybackAllocation: builder.mutation<
      any,
      {
        allocation_id: string;
        data: {
          allocation_status: string;
          phase?: string;
        };
      }
    >({
      query: ({ allocation_id, data }) => ({
        url: `finance/allocation/${allocation_id}/buyback`,
        method: "PATCH",
        data,
        headers: { "Content-Type": "application/json" },
      }),
      invalidatesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    //Checked status update
    CheckedStatusByOperation: builder.mutation<
      any,
      {
        inv_id: string;
        comment_message: string;
        reviewed_by_member?: boolean;
        reviewed_by_admin?: boolean;
      }
    >({
      query: ({
        inv_id,
        comment_message,
        reviewed_by_member,
        reviewed_by_admin,
      }) => ({
        url: `comments/invoice/${inv_id}`,
        method: "POST",
        data: {
          comment_message,
          ...(reviewed_by_member !== undefined && { reviewed_by_member }),
          ...(reviewed_by_admin !== undefined && { reviewed_by_admin }),
        },
        headers: { "Content-Type": "application/json" },
      }),
      invalidatesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    //Get invoice comments
    GetInvoiceComments: builder.query<any, string>({
      query: (invoice_id) => ({
        url: `comments/invoice/${invoice_id}`,
        method: "GET",
      }),
      providesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    // Get invoice phase statuses
    GetInvoicePhaseStatuses: builder.query<any, { phase: string }>({
      query: ({ phase }) => ({
        url: `finance/invoice-phase-statuses?phase=${encodeURIComponent(phase)}`,
        method: "GET",
      }),
      transformResponse: (response) => response?.data ?? response,
    }),

    // Apply insurer amount to an account relationship (new endpoint)
    ApplyInsurerAmount: builder.mutation<
      any,
      {
        invoice_id: string;
        insurer_id: string;
        account_relationship_id: string;
        invoice_amount: number | string;
      }
    >({
      query: ({ invoice_id, insurer_id }) => ({
        url: `operations/invoice/${invoice_id}/insurer`,
        method: "POST",
        data: {
          insurer_id,
          
        },
        headers: { "Content-Type": "application/json" },
      }),
      invalidatesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    // Get the currently applied insurer for an invoice
    GetInvoiceAppliedInsurer: builder.query<any, string>({
      query: (invoice_id) => ({
        url: `operations/invoice/${invoice_id}/insurer`,
        method: "GET",
      }),
      providesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),

    // Remove an insurer from an invoice
    RemoveInvoiceInsurer: builder.mutation<any, { invoice_id: string; insurer_id: string }>(
      {
        query: ({ invoice_id, insurer_id }) => ({
          url: `operations/invoice/${invoice_id}/insurer`,
          method: "DELETE",
          data: { insurer_id },
          headers: { "Content-Type": "application/json" },
        }),
        invalidatesTags: ["OPERATIONS_INVOICES"],
        transformResponse: (response) => response?.data,
      },
    ),

    // Update invoice finance status (approve / reject)
    UpdateInvoiceFinanceStatus: builder.mutation<
      any,
      {
        invoice_id: string;
        user_id: string;
        action: "approve" | "reject";
        comment: string;
        phase?: string;
        status?: string;
      }
    >({
      query: ({ invoice_id, user_id, action, comment, phase, status }) => ({
        url: `finance/invoices/${invoice_id}/finance-status`,
        method: "PATCH",
        data: {
          user_id,
          action,
          comment,
          finance_status: action,
          ...(comment ? { reason: comment } : {}),
          ...(phase ? { phase } : {}),
          ...(status ? { status } : {}),
        },
        headers: { "Content-Type": "application/json" },
      }),
      invalidatesTags: ["OPERATIONS_INVOICES"],
      transformResponse: (response) => response?.data,
    }),
  }),
});

export const {
  useGetInvoiceQuery,
  useGetInvestorsSummaryQuery,
  useGetTransactionLadgerQuery,
  useGetAllTransactionsQuery,
  useGetImportersQuery,
  useGetAUMDetailsQuery,
  useGetSectorQuery,
  useExportAllTransactionsMutation,
  useGetAllInvoicesQuery,
  useGetInvocieDetailsQuery,
  useGetInvoiceVerificationQuery,
  useUpdateInvoiceVerificationMutation,
  useAddinvoiceVerificationMutation,
  useGetInvoiceDetailsQuery,
  useGetInvoiceDocumentVerificationQuery,
  useCreateBillOfLadingVerificationMutation,
  useGetBillOfLadingVerificationQuery,
  useGetLatestBillOfLadingVerificationQuery,
  useReverifyBillOfLadingVerificationMutation,
  useGetAllocatedFunderDetailsQuery,
  useLazyGetAllocatedFunderDetailsQuery,
  useGetInvoiceMapInfoQuery,
  useGetAllTemplatesQuery,
  useGetTemplateDetailsMutation,
  useGetInvoiceRequestTemplateDetailsMutation,
  useUpdateInvoiceMutation,
  useUpdateInvoiceBasicInfoMutation,
  useAllInvoiceDocumentsMutation,
  useUpdateFunderBalanceMutation,
  useGetFunderDetailsQuery,
  useSubmitFunderAllocationsMutation,
  useUpdateFunderAllocationsMutation,
  useGetFunderAllocationsQuery,
  useAllocationDecisionMutation,
  useCancelAllocationMutation,
  useUpdateAllocationStatusMutation,
  usePatchAllocationMutation,
  useBuybackAllocationMutation,
  useSendEmailTemplateMutation,
  useGetAllTemplatesNOAQuery,
  useLazyGetFunderBuyerSupplierLimitDetailsQuery,
  useGetInvoiceRequestTemplatesQuery,
  useCheckedStatusByOperationMutation,
  useGetInvoiceCommentsQuery,
  useUpdateInvoiceFinanceStatusMutation,
  useGetInvoiceInstructionsForOpsQuery,
  useUpdateInvoiceInstructionsForOpsMutation,
  useAddInvoiceInstructionsForOpsMutation,
  useGetInvoiceInteractionOpsQuery,
  useGetInvoicePhaseStatusesQuery,
  useApplyInsurerAmountMutation,
  useLazyGetInvoiceAppliedInsurerQuery,
  useGetInvoiceAppliedInsurerQuery,
  useRemoveInvoiceInsurerMutation,
} = investor_v2;
